import { ActivatedRoute } from '@angular/router';
import { Component, Output, EventEmitter, ElementRef, ViewChild, AfterViewInit, Input, OnChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOptionSelectionChange } from '@angular/material/core';
import { forkJoin, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CacheService } from 'ionic-cache';

import * as $ from 'jquery';
import { Cutter_ } from '../../../service/formulas/objects';
import { CommonService } from '../../../service/common.service';
import { Utils } from '../../../service/utils';
import _ from 'lodash';
import Fuse from 'fuse.js';
import { EndmillProvider } from './endmill.service';

declare var cordova;

interface EndmillParams {
  brand: string;
  sku: string;
}

interface EndmillSearch {
  productKey: number;
  searchParameter: string;
  itemId: string;
  description: string;
  brandShortName: string;
  brandKey: number;
}

@Component({
  selector: 'section-endmill',
  templateUrl: 'endmill.html',
  providers: [EndmillProvider],
  styleUrls: ['endmill.scss']
})
export class Endmill implements AfterViewInit, OnChanges {
  private subscription: Subscription;
  @Output() edpChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() startClicked: EventEmitter<any> = new EventEmitter<any>();
  @Input('ndcDynamicInputs') endmillParams: EndmillParams;
  @ViewChild('endmillArrow', { static: true }) endmillArrow: ElementRef;
  @ViewChild('selectedToolOption', { static: true }) selectedToolOption: ElementRef;
  @ViewChild(MatAutocompleteTrigger, { static: true }) autocomplete: MatAutocompleteTrigger;

  private readonly minimumSearchLength = 2;

  public result: object = undefined;
  isApp: boolean;
  endmillFormGroup: FormGroup;
  endmills: { [productKey: number]: Cutter_ } = {};
  searchStrings: EndmillSearch[] = [];
  filteredEndmills: EndmillSearch[] = [];
  arrowDirection: boolean;
  displayFilterMessage: boolean;
  showNoResultsMessage: boolean;
  windowsDimensions: object = undefined;
  deepLinkSku: string;
  public warningIcon: boolean = false;
  private fuseSearch: any;
  private fuseSearchOptions = {
    shouldSort: false,
    threshold: 0.05,
    location: 0,
    distance: 20,
    maxPatternLength: 32,
    tokenize: true,
    keys: ['itemId', 'description', 'searchParameter']
  };
  private readonly inchesPerCentimeter = 0.0393701;

  constructor(
    private formBuilder: FormBuilder,
    private commonService: CommonService,
    private cache: CacheService,
    private utils: Utils,
    private actRoute: ActivatedRoute,
    private endmillProvider: EndmillProvider
  ) {
    this.endmillFormGroup = this.formBuilder.group({
      toolSelectionFormGroup: this.formBuilder.group({
        selectedToolOption: new FormControl('')
      })
    });

    this.isApp = this.utils.isApp();
    this.arrowDirection = false;
    this.displayFilterMessage = false;
    this.showNoResultsMessage = false;
    this.deepLinkSku = '';

    Promise.all([this.cache.getItem('endmillSearchStrings'), this.cache.getItem('endmills')])
      .then(promise => {
        this.searchStrings = promise[0];
        this.endmills = promise[1];

        this.fuseSearch = new Fuse(this.searchStrings, this.fuseSearchOptions);

        const sku: string = this.actRoute.snapshot.params['sku'];
        const brand: string = this.actRoute.snapshot.params['brand'];
        if (sku !== 'null' && brand !== 'null') {
          // itemId and brand must both match exactly
          const tool: EndmillSearch = _.find(this.searchStrings, { itemId: sku, brandShortName: brand });
          if (tool) {
            this.selectEndmill(tool);
          }
        } else if (!!this.endmillParams.sku && !!this.endmillParams.brand) {
          this.deepLinkFill(this.endmillParams.sku, this.endmillParams.brand);
        }
      })
      .catch(err => console.log(err));

    this.cache.getItem('endmillsCached').then(isCached => {
      // check if we have cached all the endmill information, if not then fetch all the endmills
      if (!isCached) {
        this.fetchAllEndmills();
      }
    });

    if (this.isApp) {
      this.cache.getItem('windowsDimensions').then(windowsDimensions => {
        this.windowsDimensions = windowsDimensions;
      });
    }
  }

  private fetchAllEndmills = () => {
    forkJoin([
      this.endmillProvider.getEndmills(),
      this.endmillProvider.getEndmillsDetails(),
      this.endmillProvider.getMaterialsDetails()
    ]).subscribe(
      ([endmills, endmillDetails, materialIds]) => {
        _.each(endmills, tool => {
          // this is data straight from database, no changing to endmill or cutter object yet
          _.extend(tool, {
            metricNumbers: {
              cutterDiameter: tool['metric'] === 1 ? tool['cutterDiameter'] : undefined,
              coreDiameter: tool['metric'] === 1 ? tool['coreDiameter'] : undefined,
              lengthOfCut: tool['metric'] === 1 ? tool['lengthOfCut'] : undefined,
              overallLength: tool['metric'] === 1 ? tool['overallLength'] : undefined,
              overallReach: tool['metric'] === 1 ? tool['overallReach'] : undefined,
              neckDiameter: tool['metric'] === 1 ? tool['neckDiameter'] : undefined,
              tip: tool['metric'] === 1 ? tool['tip'] : undefined,
              cornerRadius: tool['metric'] === 1 ? tool['cornerRadius'] : undefined
            }
          });
          if (tool['metric'] === 1) {
            tool['cutterDiameter'] = tool['cutterDiameter'] * this.inchesPerCentimeter;
            tool['coreDiameter'] = tool['coreDiameter'] * this.inchesPerCentimeter;
            tool['lengthOfCut'] = tool['lengthOfCut'] * this.inchesPerCentimeter;
            tool['overallLength'] = tool['overallLength'] * this.inchesPerCentimeter;
            tool['overallReach'] = tool['overallReach'] * this.inchesPerCentimeter;
            tool['neckDiameter'] = tool['neckDiameter'] * this.inchesPerCentimeter;
            tool['tip'] = tool['tip'] * this.inchesPerCentimeter;
            tool['cornerRadius'] = tool['cornerRadius'] * this.inchesPerCentimeter;
          }
          const toolDetail = endmillDetails[tool.id] ? endmillDetails[tool.id] : [];
          tool['searchParameter'] = toolDetail ? toolDetail.searchParameter : '';
          tool['toolImageUrl'] = toolDetail ? toolDetail.toolImageUrl : '';
          tool['toolDetails'] = toolDetail.toolDetails;
          tool['materialIds'] = materialIds[tool.id] ? materialIds[tool.id] : [];

          this.endmills[tool.id] = tool;
        });

        this.cache.saveItem('endmills', this.endmills);
        this.cache.saveItem('endmillsCached', true);
      },
      error => {
        console.log(error);
        this.cache.saveItem('endmillsCached', false);
      }
    );
  };

  ngOnChanges(changes) {
    this.endmillParams = {
      sku: changes.sku.currentValue,
      brand: changes.brand.currentValue
    };
    if (!!this.endmills && !!this.endmillParams.sku && !!this.endmillParams.brand) {
      this.deepLinkFill(this.endmillParams.sku, this.endmillParams.brand);
    }
  }

  ngAfterViewInit() {
    this.endmillArrow.nativeElement.className = 'down arrow';

    this.endmillFormGroup
      .get('toolSelectionFormGroup')
      .get('selectedToolOption')
      .valueChanges.pipe(debounceTime(800), distinctUntilChanged())
      .subscribe((result: any) => {
        if (typeof result === 'object') {
          this.filteredEndmills = this.fuseSearch.search(result.itemId);
          result = result.searchParameter ? result.searchParameter : result.itemId + ' (' + result.description + ')';
        } else {
          if (result.length < this.minimumSearchLength) {
            this.filteredEndmills = [];
          } else {
            this.filteredEndmills = this.fuseSearch.search(result.toString());
            this.filteredEndmills = this.filteredEndmills.splice(0, 99);
            this.showNoResultsMessage = !this.filteredEndmills.length;
          }
        }
      });

    this.subscription = this.commonService.notifyObservable$.subscribe(res => {
      if (res.hasOwnProperty('option')) {
        if (res.option === 'deeplink') {
          this.deepLinkSku = res.value;
        }
        if (res.option === 'logout') {
          this.subscription.unsubscribe();
        }
        if (res.option === 'reset' || res.option === 'resetTool') {
          this.endmillFormGroup.reset({}, { emitEvent: false });
          this.result = undefined; //reset
          this.warningIcon = false;
          this.arrowDirection = false;
          this.filteredEndmills = [];
          this.showNoResultsMessage = false;

          // resetting the place holder do library's own bug
          let selectedToolOptionLabel = $(this.selectedToolOption.nativeElement)
            .parents('.mat-form-field-infix')
            .find('.mat-form-field-label-wrapper')
            .find('.mat-form-field-label');
          if (
            !selectedToolOptionLabel.hasClass('mat-empty') &&
            !selectedToolOptionLabel.hasClass('mat-form-field-empty')
          ) {
            selectedToolOptionLabel.addClass('mat-empty').addClass('mat-form-field-empty');
          }
        }
        if (res.option === 'aggressiveWarning') {
          this.warningIcon = true;
        }
      }
    });
  }

  setArrowDirection = (arrow: string) => {
    if (arrow === 'up') {
      this.arrowDirection = true;
      this.endmillArrow.nativeElement.className = 'up arrow';
      this.displayFilterMessage = true;
    } else {
      // Select tool if there's only one option
      if (this.filteredEndmills && this.filteredEndmills.length === 1) {
        this.selectEndmill(this.filteredEndmills[0]);
      }

      this.arrowDirection = false;
      this.endmillArrow.nativeElement.className = 'down arrow';
      this.displayFilterMessage = false;
    }
  };

  handleToolSubmit = () => {
    // Select tool if there's only one option
    if (this.filteredEndmills && this.filteredEndmills.length === 1) {
      this.selectEndmill(this.filteredEndmills[0]);
    }
  };

  selectEndmill = (selectedEndmill: EndmillSearch) => {
    // will check for endmill here;
    this.endmillFormGroup
      .get('toolSelectionFormGroup')
      .get('selectedToolOption')
      .patchValue(
        selectedEndmill.searchParameter
          ? selectedEndmill.searchParameter
          : selectedEndmill.itemId + ' (' + selectedEndmill.description + ')',
        {
          emitEvent: false
        }
      );
    this.autocomplete.closePanel();
    this.arrowDirection = false;
    this.warningIcon = false;

    if (this.endmills[selectedEndmill.productKey]) {
      this.setEndmill(this.endmills[selectedEndmill.productKey]);
    } else {
      forkJoin([
        this.endmillProvider.fetchEndmill(selectedEndmill.productKey),
        this.endmillProvider.fetchMaterialIds(selectedEndmill.productKey),
        this.endmillProvider.fetchToolDetailsForEndmill(selectedEndmill.productKey)
      ]).subscribe(
        ([cutter, materialIds, toolDetails]) => {
          const newEndmill: Cutter_ = cutter;

          _.extend(newEndmill, {
            metricNumbers: {
              cutterDiameter: newEndmill['metric'] === 1 ? newEndmill['cutterDiameter'] : undefined,
              coreDiameter: newEndmill['metric'] === 1 ? newEndmill['coreDiameter'] : undefined,
              lengthOfCut: newEndmill['metric'] === 1 ? newEndmill['lengthOfCut'] : undefined,
              overallLength: newEndmill['metric'] === 1 ? newEndmill['overallLength'] : undefined,
              overallReach: newEndmill['metric'] === 1 ? newEndmill['overallReach'] : undefined,
              neckDiameter: newEndmill['metric'] === 1 ? newEndmill['neckDiameter'] : undefined,
              tip: newEndmill['metric'] === 1 ? newEndmill['tip'] : undefined,
              cornerRadius: newEndmill['metric'] === 1 ? newEndmill['cornerRadius'] : undefined
            }
          });

          if (newEndmill['metric'] === 1) {
            newEndmill['cutterDiameter'] = newEndmill['cutterDiameter'] * this.inchesPerCentimeter;
            newEndmill['coreDiameter'] = newEndmill['coreDiameter'] * this.inchesPerCentimeter;
            newEndmill['lengthOfCut'] = newEndmill['lengthOfCut'] * this.inchesPerCentimeter;
            newEndmill['overallLength'] = newEndmill['overallLength'] * this.inchesPerCentimeter;
            newEndmill['overallReach'] = newEndmill['overallReach'] * this.inchesPerCentimeter;
            newEndmill['neckDiameter'] = newEndmill['neckDiameter'] * this.inchesPerCentimeter;
            newEndmill['tip'] = newEndmill['tip'] * this.inchesPerCentimeter;
            newEndmill['cornerRadius'] = newEndmill['cornerRadius'] * this.inchesPerCentimeter;
          }

          newEndmill.materialIds = materialIds;
          newEndmill.searchParameter = toolDetails ? toolDetails.searchParameter : '';
          newEndmill.toolImageUrl = toolDetails ? toolDetails.toolImageUrl : '';
          newEndmill.toolDetails = toolDetails.toolDetails;
          this.endmills[cutter.id] = newEndmill;
          this.cache.saveItem('endmills', this.endmills);
          this.setEndmill(newEndmill);
        },
        error => console.log(error)
      );
    }
  };

  setEndmill = (value: Cutter_) => {
    //Added back in the clear element and set arrow when user selects a tool #
    //To get the user selection before blur clears out the drop down we have to use mousedown or touchstart
    //Android devices captures both events and causing a double click and setting the wrong tool # so keep this here for now
    // Reset workholdine security slider when different tool is selected
    this.resetWorkHoldingSecurity();
    if (value) {
      $('#selectedToolOption').blur();
      this.autocomplete.closePanel();
      this.endmillFormGroup
        .get('toolSelectionFormGroup')
        .get('selectedToolOption')
        .patchValue(value.searchParameter ? value.searchParameter : value.itemId + ' (' + value.description + ')', {
          emitEvent: false
        });
      this.arrowDirection = false;
      this.warningIcon = false;
      // this is fine, ultimately it only gets fired once
      this.result = value;

      this.edpChange.emit(value);
      this.filteredEndmills = [];

      if (this.isApp) {
        this.utils.apptentiveEngage('Tool Number');
      }
      this.commonService.notifyOther({
        option: 'checkToGetParamsAndRecs'
      });

      setTimeout(() => {
        this.commonService.notifyOther({
          option: 'recommendationsGeneratedForLoggingData',
          value: {
            logLocation: 'tool change'
          }
        });
      });
    }
  };

  navigateToToolPage = url => {
    if (this.isApp) {
      cordova.InAppBrowser.open(url, '_system');
    } else {
      window.open(url);
    }
  };

  deepLinkFill(sku: string, brand: string) {
    const tool: EndmillSearch = _.find(this.searchStrings, { itemId: sku, brandShortName: brand });
    if (tool) {
      this.selectEndmill(tool);
    } else {
      alert('Cannot find the tool that was selected.');
    }
    this.startClicked.emit(1);
    $('.deepLinkSnackBar').slideUp('500');
    this.commonService.notifyOther({
      option: 'deepLinkSearchFinish'
    });
  }

  resetWorkHoldingSecurity = () => {
    this.commonService.notifyOther({
      option: 'resetWorkHoldingSecurity'
    });
  };
}
